import PropTypes from "prop-types";
import React, { useContext, useReducer } from "react";

import {
  SET_GEOFENCES_NAME,
  SET_GEOFENCES_VISIBILITY_OPTIONS,
  SET_GEOFENCES_SIDEWALKS_EDIT_MODE,
  ADD_GEOFENCES_LAYER,
  SET_GEOFENCES_LAYERS,
  SET_GEOFENCES_SELECTED_LAYER,
  SET_GEOFENCES_SELECTED_ZONE,
  INIT_GEOFENCES_DATA,
  SET_GEOFENCES_ERROR,
  SET_GEOFENCES_KEY,
  SET_SCHEDULED_RULES,
  DELETE_SCHEDULE_RULE,
} from "../types";

import GeofencesContext from "./geofencesContext";
import geofencesReducer from "./geofencesReducer";

import { StringUtils } from "../../util";
import {
  createLayer,
  createZone,
  zoneFlagsFromLayerType,
  parseAPIZonesList,
  geoJSONGeometryFromPolygon,
  getPolygonFromH3Indexes,
  bulkUploadLayer,
} from "../../services/geofences.service";
import {
  GeofencesSpeedLayers,
  GeofencesIncentiveLayers,
  GeofencesResolutionRange,
  GeofencesSpeedRange,
  GeofencesIncentiveRange,
  GeofencesEditModes,
  GeofencesLayerTypes,
  MaxConcurrentRequests,
} from "../../constants";
import API from "../../API";
import FleetContext from "../Fleet/fleetContext";

const GeofencesState = ({
  children,
  name: initialName,
  layers: initialLayers,
  selectedLayer: initialSelectedLayer,
  selectedZone: initialSelectedZone,
  sidewalksMode,
}) => {
  const { activeFleet } = useContext(FleetContext);

  const initialState = {
    key: null,
    name: initialName || {},
    showPolygons: true,
    // NOTE: Vienna fleet has crazy geofences, so don't show hexes by default there
    showHexes: activeFleet?.uuid4 !== window.REACT_APP_VIENNA_FLEET_UUID,
    layers: initialLayers || null,
    scheduledRules: null,
    selectedLayer: initialSelectedLayer || {
      key: null,
      editMode: null,
      resolution: sidewalksMode
        ? GeofencesResolutionRange.defaultSidewalks
        : GeofencesResolutionRange.default,
    },
    selectedZone: initialSelectedZone || {
      key: null,
      parent: null,
      editMode: null,
      resolution: sidewalksMode
        ? GeofencesResolutionRange.defaultSidewalks
        : GeofencesResolutionRange.default,
    },
    error: null,
    sidewalksEditMode: GeofencesEditModes.default,
  };

  const [state, dispatch] = useReducer(geofencesReducer, initialState);

  // Loading Data

  const fetchFleetGeofences = async (fleetId) => {
    try {
      const geofences = await API.geofences.listAll({
        fleet: fleetId,
        hide_sidewalks: !sidewalksMode,
        only_sidewalks: sidewalksMode,
      });

      const filteredGeofences = geofences.filter(
        (geofence) => geofence.rules && geofence.rules.length,
      );

      const layers = parseAPIZonesList(filteredGeofences);

      initGeofencesData({ name: "Fleet Data", editable: false, layers });
    } catch (err) {
      console.error(err);
      dispatch({
        type: SET_GEOFENCES_ERROR,
        payload: err.toString(),
      });
    }
  };

  const loadScheduledRules = (activeGeofence) => {
    const geofenceRules = activeGeofence.rules;
    const scheduledRules = geofenceRules.filter((rule) => !!rule.schedule);

    dispatch({
      type: SET_SCHEDULED_RULES,
      payload: scheduledRules,
    });
  };

  const initGeofencesData = ({ key, name, editable, layers }) => {
    setName({ name, editable });

    dispatch({
      type: SET_GEOFENCES_KEY,
      payload: key,
    });

    dispatch({
      type: SET_GEOFENCES_LAYERS,
      payload: layers,
    });
  };

  const clearGeofencesData = () => {
    dispatch({
      type: INIT_GEOFENCES_DATA,
      payload: initialState,
    });
  };

  // Error

  const setError = (error) => {
    dispatch({
      type: SET_GEOFENCES_ERROR,
      payload: error,
    });
  };

  const clearError = () => {
    dispatch({
      type: SET_GEOFENCES_ERROR,
      payload: null,
    });
  };

  // General Settings

  const setName = ({ name, editable = state.name.editable }) => {
    dispatch({
      type: SET_GEOFENCES_NAME,
      payload: { value: name, editable },
    });
  };

  const setVisibilityOptions = (showPolygons, showHexes) => {
    dispatch({
      type: SET_GEOFENCES_VISIBILITY_OPTIONS,
      payload: { showPolygons, showHexes },
    });
  };

  const setSidewalksEditMode = (mode) => {
    dispatch({
      type: SET_GEOFENCES_SIDEWALKS_EDIT_MODE,
      payload: { mode },
    });
  };

  // Layer Actions

  const addLayer = () => {
    dispatch({
      type: ADD_GEOFENCES_LAYER,
      payload: { ...createLayer() },
    });
  };

  const deleteLayer = (key) => {
    const layers = { ...state.layers };

    delete layers[key];

    if (state.selectedLayer.key === key) {
      dispatch({
        type: SET_GEOFENCES_SELECTED_LAYER,
        payload: {},
      });
    } else if (state.selectedZone.parent === key) {
      dispatch({
        type: SET_GEOFENCES_SELECTED_ZONE,
        payload: {},
      });
    }

    dispatch({
      type: SET_GEOFENCES_LAYERS,
      payload: layers,
    });
  };

  const updateLayer = (key, value = {}) => {
    const layers = { ...state.layers };
    const layer = layers[key];

    const { hidden = layer.hidden, type = layer.type, name = layer.name } = value;

    let speed = Object.keys(value).includes("speed") ? value.speed : layers[key].speed;

    let incentiveAmount = Object.keys(value).includes("incentive")
      ? value.incentive.amount
      : layers[key].incentive?.amount;

    if (value.type) {
      speed = GeofencesSpeedLayers.includes(value.type) ? GeofencesSpeedRange.default : null;
      incentiveAmount = GeofencesIncentiveLayers.includes(value.type)
        ? GeofencesIncentiveRange.default
        : null;
    }

    layers[key] = {
      ...layer,
      hidden,
      type,
      name,
      speed,
      incentive: {
        ...layers[key].incentive,
        amount: incentiveAmount,
        currency: value.incentive?.currency || "",
      },
    };

    if (
      (value.type && value.type !== layer.type) ||
      (value.speed && value.speed !== layer.speed) ||
      (value.incentive && incentiveAmount !== layer.incentive?.amount)
    ) {
      Object.keys(layer.zones).forEach((zoneKey) => {
        layer.zones[zoneKey].persisted = false;
      });
    }

    dispatch({
      type: SET_GEOFENCES_LAYERS,
      payload: layers,
    });
  };

  const uploadLayer = async ({ key, marketUrl, fleetUrl }) => {
    const maxRequestHexes = Number(window.REACT_APP_MAX_REQUEST_HEXES);
    const layer = state.layers[key];
    const payload = {
      marketUrl,
      fleetUrl,
      speedLimit: layer.speed === "" ? null : layer.speed,
      ...zoneFlagsFromLayerType(layer.type),
      isSidewalk: sidewalksMode,
    };

    try {
      const allNewZones = Object.values(layer.zones).reduce((res, zone) => {
        if (zone.persisted || zone.id || !Object.keys(zone.hexes).length) return res;
        return [...res, zone];
      }, []);

      if (!allNewZones.length) return;

      // Split all zones (active or inactive) into chunks based on amount of hexes
      // One request should contain maxRequestHexes hexes or less
      const splitZonesByHexesCount = (active) => {
        const resultZones = [];

        let hexesCount = 0;
        let index = 0;

        for (let i = 0; i < allNewZones.length; i++) {
          const zone = allNewZones[i];
          const { length } = Object.keys(zone.hexes);

          if (length <= maxRequestHexes && zone.active === active) {
            hexesCount += length;

            if (hexesCount > maxRequestHexes) {
              hexesCount = length;
              index += 1;
            }

            if (resultZones.length === index) {
              resultZones.push([]);
            }

            resultZones[index].push(zone);
          }
        }

        return resultZones;
      };

      // Send multiple requests and wait for the result
      // Only MaxConcurrentRequests = 5 requests can be sent at the same time
      const createGeofencesFromZones = async (allZones, active) => {
        let data = [];

        while (allZones.length) {
          const promises = allZones
            .splice(0, MaxConcurrentRequests)
            .map((zones) =>
              bulkUploadLayer({ ...payload, isActive: active, zones }).then((response) => response),
            );
          // eslint-disable-next-line no-await-in-loop
          const response = await Promise.all(promises);

          data = data.concat(response);
        }

        return {
          geofences: data.map((d) => d.geofences).flat(1),
          rules: data.map((d) => d.rules).flat(1),
        };
      };

      const newActiveZones = splitZonesByHexesCount(true);
      const newInactiveZones = splitZonesByHexesCount(false);

      const activeData = await createGeofencesFromZones(newActiveZones, true);
      const inactiveData = await createGeofencesFromZones(newInactiveZones, false);

      // Update the zones in the UI with returned data
      allNewZones.forEach((zone) => {
        const { length } = Object.keys(zone.hexes);

        if (length <= maxRequestHexes) {
          // All zones are divided into two arrays (active and inactive)
          // Splice is used to maintain the right order when joining the arrays back together
          const data = zone.active ? activeData : inactiveData;
          const geofence = data.geofences.splice(0, 1)[0];
          const rule = data.rules.splice(0, 1)[0];

          layer.zones[zone.key] = {
            ...zone,
            id: geofence?.id,
            ruleId: StringUtils.uuidFromUrl(rule.url),
            polygon: zone.polygon || getPolygonFromH3Indexes(Object.keys(zone.hexes)),
            persisted: true,
          };
        }
      });

      dispatch({
        type: SET_GEOFENCES_LAYERS,
        payload: {
          ...state.layers,
          [key]: layer,
        },
      });
    } catch (err) {
      console.error(err);
      dispatch({
        type: SET_GEOFENCES_ERROR,
        payload: err.toString(),
      });
    }
  };

  // Layer Panel Actions

  const selectLayer = (key) => {
    if (state.selectedLayer.key === key) return;

    if (!state.showPolygons) {
      setVisibilityOptions(true, state.showHexes);
    }
    if (key && state.layers[key].hidden) {
      updateLayer(key, { hidden: false });
    }

    dispatch({
      type: SET_GEOFENCES_SELECTED_LAYER,
      payload: { ...initialState.selectedLayer, key },
    });
  };

  // Possible parameters: editMode, resolution
  const updateSelectedLayer = (value = {}) => {
    dispatch({
      type: SET_GEOFENCES_SELECTED_LAYER,
      payload: { ...state.selectedLayer, ...value },
    });
  };

  // Zone Actions

  const addZone = (parent, { name, active, polygon, hexes, selected, created } = {}) => {
    const layers = { ...state.layers };
    const zone = createZone();

    let parentKey = parent;

    if (!parent) {
      let defaultLayer = Object.values(layers).find((l) => l.type === GeofencesLayerTypes.none);

      if (!defaultLayer) {
        defaultLayer = { ...createLayer() };
        layers[defaultLayer.key] = defaultLayer;
      }
      parentKey = defaultLayer.key;
    }

    layers[parentKey].zones[zone.key] = {
      ...zone,
      name: name || zone.name,
      active: active || zone.active,
      polygon: polygon || zone.polygon,
      hexes: hexes || zone.hexes,
      created: created || zone.created,
    };

    dispatch({
      type: SET_GEOFENCES_LAYERS,
      payload: layers,
    });

    if (selected) {
      selectZone({ key: zone.key, parent: parentKey, editMode: GeofencesEditModes.hex });
    }
  };

  const deleteZone = async ({ key, parent }) => {
    const layers = { ...state.layers };
    const layer = layers[parent];
    const zone = layer.zones[key];

    if (zone.id) {
      try {
        await API.geofences.deleteGeofence(zone.id, !sidewalksMode);
        await API.geofences.deleteRule(zone.ruleId);
      } catch (err) {
        console.error(err);
        dispatch({
          type: SET_GEOFENCES_ERROR,
          payload: err.toString(),
        });
      }
    }

    delete layers[parent].zones[key];

    dispatch({
      type: SET_GEOFENCES_SELECTED_ZONE,
      payload: { key: null, parent: null },
    });

    dispatch({
      type: SET_GEOFENCES_LAYERS,
      payload: layers,
    });
  };

  const updateZone = (key, parent, value = {}, preservePersistedStatus = false) => {
    const layers = { ...state.layers };
    const zone = layers[parent].zones[key];

    if (!zone) return;

    const {
      id = zone.id,
      ruleId = zone.ruleId,
      name = zone.name,
      hidden = zone.hidden,
      active = zone.active,
      polygon = zone.polygon,
      hexes = zone.hexes,
      persisted = preservePersistedStatus ? zone.persisted : false,
    } = value;

    layers[parent].zones[key] = {
      ...zone,
      id,
      ruleId,
      name,
      active,
      hidden,
      polygon,
      hexes,
      persisted,
    };

    dispatch({
      type: SET_GEOFENCES_LAYERS,
      payload: layers,
    });
  };

  const updateZoneLayer = ({ key, oldParent, newParent }) => {
    const layers = { ...state.layers };

    layers[newParent].zones[key] = {
      ...layers[oldParent].zones[key],
      persisted: false,
    };

    delete layers[oldParent].zones[key];

    dispatch({
      type: SET_GEOFENCES_SELECTED_ZONE,
      payload: { ...state.selectedZone, parent: newParent, editMode: null },
    });

    dispatch({
      type: SET_GEOFENCES_LAYERS,
      payload: layers,
    });
  };

  const createAPIZone = async ({
    marketUrl,
    fleetUrl,
    name,
    active,
    h3Indexes,
    polygon,
    speedLimit,
    isOperationalZone,
    noParking,
    allowParking,
    limitedParking,
    isWarehouse,
    isSidewalk,
    incentive,
  } = {}) => {
    const geofencePayload = {
      name,
      fleet: fleetUrl,
      h3_indexes: h3Indexes,
      is_operational_zone: isOperationalZone,
    };

    const rulePayload = {
      active,
      market: marketUrl,
      vehicle_type: "vehicle_api.scooter",
      speed_limit: speedLimit,
      start_time: null,
      end_time: null,
      no_parking: noParking,
      allow_parking: allowParking,
      limited_parking: limitedParking,
      is_warehouse: isWarehouse,
      is_sidewalk: isSidewalk,
    };

    if (polygon) {
      geofencePayload.polygon = geoJSONGeometryFromPolygon(polygon);
    }

    if (incentive.amount) {
      const parkingIncentive = await API.billing.createParkingIncentive({
        amount: incentive.amount ? parseInt(incentive.amount, 10) : null,
        currency: incentive.currency,
      });

      rulePayload.parking_incentive_id = parkingIncentive.id;
    }

    const geofence = await API.geofences.createGeofence(geofencePayload);
    const rule = await API.geofences.createRule(rulePayload);

    const ruleId = rule.url.split("/").pop();
    const ruleMap = await API.geofences.createRuleMap({
      ruleId,
      geofenceId: geofence.id,
    });

    return { geofence, rule, ruleMap };
  };

  const createAPIScheduledGeofence = async ({
    marketUrl,
    fleetUrl,
    name,
    zoneId,
    active,
    h3Indexes,
    polygon,
    speedLimit,
    isOperationalZone,
    noParking,
    allowParking,
    limitedParking,
    isWarehouse,
    incentive,
    scheduleName,
    rules,
  } = {}) => {
    const geofencePayload = {
      id: zoneId,
      name,
      fleet: fleetUrl,
      h3_indexes: h3Indexes,
      is_operational_zone: isOperationalZone,
    };

    if (allowParking && polygon) {
      geofencePayload.polygon = geoJSONGeometryFromPolygon(polygon);
    }

    const rulePayloads = await Promise.all(
      rules.map(async (rule) => {
        const { startTime, endTime, months, month_days, week_days } = rule;
        // eslint:
        const payload = {
          start_time: startTime,
          end_time: endTime,
          months: months?.length ? months : null,
          month_days: month_days?.length ? month_days : null, // eslint-disable-line
          week_days: week_days?.length ? week_days : null, // eslint-disable-line
          active,
          market: marketUrl,
          vehicle_type: "vehicle_api.scooter",
          speed_limit: speedLimit,
          no_parking: noParking,
          allow_parking: allowParking,
          limited_parking: limitedParking,
          is_warehouse: isWarehouse,
        };

        if (incentive.amount) {
          const parkingIncentive = await API.billing.createParkingIncentive({
            amount: incentive.amount ? parseInt(incentive.amount, 10) : null,
            currency: incentive.currency,
          });

          payload.parking_incentive_id = parkingIncentive.id;
        }
        return payload;
      }),
    );

    const scheduledGeofence = await API.geofences.createScheduledGeofence({
      geofence: geofencePayload,
      rules: rulePayloads,
      schedule: { name: scheduleName },
    });
    loadScheduledRules(scheduledGeofence.geofence);

    return scheduledGeofence;
  };

  const deleteAPISchedule = async ({ geofenceId, scheduleId }) => {
    const data = { geofenceId, scheduleId };
    await API.geofences.deleteSchedule(data);

    dispatch({
      type: DELETE_SCHEDULE_RULE,
      payload: scheduleId,
    });
  };

  const updateAPIZone = async (
    geofenceId,
    {
      name,
      active,
      h3Indexes,
      polygon,
      speedLimit,
      isOperationalZone,
      noParking,
      allowParking,
      limitedParking,
      isWarehouse,
      isSidewalk,
      incentive,
    } = {},
  ) => {
    const geofencePayload = {
      name,
      h3_indexes: h3Indexes,
      is_operational_zone: isOperationalZone,
    };

    const rulePayload = {
      active,
      speed_limit: speedLimit,
      no_parking: noParking,
      allow_parking: allowParking,
      limited_parking: limitedParking,
      is_warehouse: isWarehouse,
      is_sidewalk: isSidewalk,
      parking_incentive_id: null,
    };

    if (polygon) {
      geofencePayload.polygon = geoJSONGeometryFromPolygon(polygon);
    }

    if (incentive.amount) {
      const parkingIncentive = await API.billing.createParkingIncentive({
        amount: incentive.amount ? parseInt(incentive.amount, 10) : null,
        currency: incentive.currency,
      });

      rulePayload.parking_incentive_id = parkingIncentive.id;
    }

    const geofence = await API.geofences.updateGeofence(
      geofenceId,
      geofencePayload,
      !sidewalksMode,
    );

    const freshRuleId = StringUtils.uuidFromUrl(geofence.rules[0].url);

    const rule = await API.geofences.updateRule(freshRuleId, rulePayload);

    return { geofence, rule };
  };

  const uploadZone = async ({ key, parent, marketUrl, fleetUrl }) => {
    const layer = state.layers[parent];
    const zone = layer.zones[key];

    if (zone.persisted) return;

    const payload = {
      marketUrl,
      fleetUrl,
      name: zone.name,
      active: zone.active,
      polygon: zone.polygon,
      h3Indexes: Object.keys(zone.hexes),
      speedLimit: layer.speed === "" ? null : layer.speed,
      incentive: layer.incentive || {},
      ...zoneFlagsFromLayerType(layer.type),
      isSidewalk: sidewalksMode,
    };

    try {
      const { geofence, rule } = zone.id
        ? await updateAPIZone(zone.id, payload)
        : await createAPIZone(payload);
      updateZone(key, parent, {
        id: geofence.id,
        ruleId: StringUtils.uuidFromUrl(rule.url),
        polygon: zone.polygon || getPolygonFromH3Indexes(Object.keys(zone.hexes)),
        persisted: true,
      });
    } catch (err) {
      console.error(err);
      dispatch({
        type: SET_GEOFENCES_ERROR,
        payload: err.toString(),
      });
    }
  };

  // Zone Panel Actions

  const selectZone = ({ key, parent, editMode }) => {
    if (state.selectedZone.key === key && state.selectedZone.parent === parent) return;

    if (key && state.layers[parent].zones[key].hidden) {
      updateZone(key, parent, { hidden: false });
    }

    if (parent) {
      if (state.layers[parent].hidden) {
        updateLayer(parent, { hidden: false });
      }

      const hasHexes = !!Object.keys(state.layers[parent].zones[key].hexes).length;

      if (hasHexes && !state.showHexes && !state.showPolygons) {
        setVisibilityOptions(state.showPolygons, true);
      }
      if (!hasHexes && !state.showPolygons) {
        setVisibilityOptions(true, state.showHexes);
      }
    }

    dispatch({
      type: SET_GEOFENCES_SELECTED_ZONE,
      payload: { ...initialState.selectedZone, key, parent, editMode },
    });
  };

  const updateSelectedZone = (value = {}) => {
    const { editMode = state.selectedZone.editMode, resolution = state.selectedZone.resolution } =
      value;

    if (value.editMode === GeofencesEditModes.hex && !state.showHexes) {
      setVisibilityOptions(state.showPolygons, true);
    }

    if (
      (value.editMode === GeofencesEditModes.polygonCreate ||
        value.editMode === GeofencesEditModes.polygonEdit) &&
      !state.showPolygons
    ) {
      setVisibilityOptions(true, state.showHexes);
    }

    dispatch({
      type: SET_GEOFENCES_SELECTED_ZONE,
      payload: {
        ...state.selectedZone,
        editMode,
        resolution,
      },
    });
  };

  return (
    <GeofencesContext.Provider
      value={{
        key: state.key,
        name: state.name,
        layers: state.layers,
        selectedLayer: state.selectedLayer,
        selectedZone: state.selectedZone,
        showPolygons: state.showPolygons,
        showHexes: state.showHexes,
        scheduledRules: state.scheduledRules,
        sidewalksEditMode: state.sidewalksEditMode,

        // Error
        error: state.error,
        setError,
        clearError,

        // Loading Data
        fetchFleetGeofences,
        loadScheduledRules,
        initGeofencesData,
        clearGeofencesData,

        // General Settings
        setName,
        setVisibilityOptions,
        setSidewalksEditMode,

        // Layer Actions
        addLayer,
        deleteLayer,
        updateLayer,
        uploadLayer,

        // Layer Panel Actions
        selectLayer,
        updateSelectedLayer,

        // Zone Actions
        createAPIScheduledGeofence,
        addZone,
        deleteZone,
        updateZone,
        updateZoneLayer,
        uploadZone,
        deleteAPISchedule,

        // Zone Panel Actions
        selectZone,
        updateSelectedZone,
      }}
    >
      {children}
    </GeofencesContext.Provider>
  );
};

GeofencesState.propTypes = {
  children: PropTypes.node.isRequired,
  name: PropTypes.object,
  layers: PropTypes.object,
  selectedLayer: PropTypes.object,
  selectedZone: PropTypes.object,
  sidewalksMode: PropTypes.bool,
};

GeofencesState.defaultProps = {
  name: null,
  layers: null,
  selectedLayer: null,
  selectedZone: null,
  sidewalksMode: false,
};

export default GeofencesState;
